import { useState } from 'react';
import IcImage from '@/assets/icons/ic-image.svg';
import { cn, sanitizeHtml, stripHtmlTags } from '@/lib';
import { formatCurrency } from '@/lib/currency';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import type { FunnelPageRelationship } from '../../../logic';

interface FunnelJourneyPageCardProps {
    page: FunnelPageItem;
    productInfo: FunnelPageProductInfo | undefined;
    stepNumber: number;
    totalSteps: number;
    relationship: FunnelPageRelationship | undefined;
    isFirstStep: boolean;
    onBuy: () => void;
    onSkip: () => void;
}

type SwipeDir = 'left' | 'right' | null;

export const FunnelJourneyPageCard = ({
    page,
    productInfo,
    stepNumber,
    totalSteps,
    relationship,
    isFirstStep,
    onBuy,
    onSkip,
}: FunnelJourneyPageCardProps) => {
    const [swipeDir, setSwipeDir] = useState<SwipeDir>(null);

    const title = productInfo?.title || page.title || 'Untitled Product';
    const priceLabel =
        typeof productInfo?.price === 'number' && productInfo.price > 0
            ? formatCurrency(productInfo.price, productInfo.currency ?? 'USD')
            : null;
    const ctaLabel = page.funnelCtaText
        ? stripHtmlTags(page.funnelCtaText)
        : priceLabel
          ? `Purchase for ${priceLabel}`
          : 'Purchase';
    const rawDescription = productInfo?.description ?? page.description;
    const description = rawDescription ? stripHtmlTags(rawDescription) : null;
    const isFirstPage = relationship?.kind === 'lead_capture';
    const bannerHtml =
        !isFirstPage && page.topBannerText && stripHtmlTags(page.topBannerText)
            ? sanitizeHtml(page.topBannerText)
            : null;
    const image = productInfo?.image;
    const hasImage = !!image;

    const isSwiping = swipeDir !== null;

    const handleBuy = () => {
        if (isSwiping) return;
        setSwipeDir('right');
    };

    const handleSkip = () => {
        if (isSwiping) return;
        setSwipeDir('left');
    };

    const handleAnimationEnd = () => {
        if (swipeDir === 'right') onBuy();
        else if (swipeDir === 'left') onSkip();
    };

    return (
        <div className='xl:shadow-stan flex w-full flex-col justify-center gap-3 rounded-lg xl:p-4'>
            {/* Top banner */}
            {bannerHtml && (
                <div
                    className={cn(
                        'bg-canvas border-brand-soft text-brand-main text-caption-1-bold rounded-md border px-4 py-2 text-center transition-opacity duration-200',
                        '[&_*+h1]:mt-3 [&_*+h2]:mt-3 [&_*+h3]:mt-3 [&_h1+*]:mt-2 [&_h2+*]:mt-2 [&_h3+*]:mt-2 [&_p+p]:mt-2',
                        isSwiping && 'opacity-0'
                    )}
                    dangerouslySetInnerHTML={{ __html: bannerHtml }}
                />
            )}

            <div
                onAnimationEnd={handleAnimationEnd}
                className={cn(
                    'bg-canvas shadow-stan relative mx-auto w-full max-w-lg overflow-hidden rounded-2xl',
                    swipeDir === null && 'animate-card-enter',
                    swipeDir === 'left' && 'animate-card-swipe-left',
                    swipeDir === 'right' && 'animate-card-swipe-right'
                )}
            >
                {/* Image area — tall, prominent */}
                <div className='bg-neutral-subtle relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden'>
                    {image ? (
                        <img src={image} alt={title} className='size-full object-cover' />
                    ) : (
                        <IcImage className='text-gray size-16 opacity-20' />
                    )}

                    {/* Gradient overlay for text legibility */}
                    {hasImage && (
                        <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent' />
                    )}

                    {/* Page counter pill — top left */}
                    <div className='absolute top-3 left-3'>
                        <span className='text-caption-2-bold rounded-full bg-black/40 px-2.5 py-1 text-white backdrop-blur-sm'>
                            Page {stepNumber} of {totalSteps}
                        </span>
                    </div>

                    {/* Price pill — top right */}
                    {priceLabel && (
                        <div className='absolute top-3 right-3'>
                            <span className='bg-brand-main text-on-brand !text-caption-2-bold rounded-full px-2.5 py-1'>
                                {priceLabel}
                            </span>
                        </div>
                    )}

                    {/* Title overlay at bottom of image */}
                    {hasImage && (
                        <div className='absolute right-0 bottom-0 left-0 p-4'>
                            <h3 className='text-heading-h4 line-clamp-2 text-white'>{title}</h3>
                        </div>
                    )}
                </div>

                {/* Body — below image */}
                {(!hasImage || description) && (
                    <div className='flex flex-col gap-2 p-4'>
                        {!hasImage && <h3 className='text-heading-h4 text-dark line-clamp-2'>{title}</h3>}
                        {description && <p className='text-body-sm text-gray line-clamp-3'>{description}</p>}
                    </div>
                )}
            </div>

            {/* Actions — outside the card, below it */}
            <div
                className={cn(
                    'mx-auto flex w-full max-w-lg gap-2 transition-opacity duration-200',
                    isSwiping && 'pointer-events-none opacity-0'
                )}
            >
                {!isFirstStep && (
                    <button
                        type='button'
                        onClick={handleSkip}
                        disabled={isSwiping}
                        className={cn(
                            'text-gray text-label-sm-bold border-neutral-subtle border',
                            'flex flex-1 cursor-pointer items-center justify-center rounded-xl px-4 py-3',
                            'transition-all duration-150',
                            'hover:bg-neutral-subtle hover:text-dark hover:border-neutral-primary',
                            'active:scale-[0.97]'
                        )}
                    >
                        Skip
                    </button>
                )}
                <button
                    type='button'
                    onClick={handleBuy}
                    disabled={isSwiping}
                    className={cn(
                        'bg-brand-main text-label-sm-bold text-white',
                        'flex cursor-pointer items-center justify-center rounded-xl px-5 py-3',
                        isFirstStep ? 'w-full' : 'flex-[2]',
                        'shadow-brand transition-all duration-150',
                        'hover:bg-brand-hover hover:-translate-y-px hover:shadow-lg',
                        'active:bg-brand-pressed active:translate-y-0 active:scale-[0.97] active:shadow-none'
                    )}
                >
                    {ctaLabel}
                </button>
            </div>

            {isFirstStep && (
                <p className='text-caption-2-regular text-gray text-center'>The first page cannot be skipped.</p>
            )}
        </div>
    );
};
